import type {
  GridConfig,
  ComputedGridConfig,
  GridDataItemRaw,
  GridColumnCellValue,
  GridColumnEditorType,
  GridEditableColumn,
  GridMappableColumn,
  GridColumn,
  ComputedGridColumn,
  ComputedGridConfigOptions,
} from "@/types/grid";
import { compareInputValues } from "@base/components/input/base/utils";

export const defaultEditorCells = new Map<GridColumnEditorType, string>([
  ["text", "InputTextCell"],
  ["numeric", "InputNumericCell"],
  ["boolean", "InputBooleanCell"],
  ["date", "InputDatePickerCell"],
  ["select", "InputSelectCell"],
  ["multiselect", "InputMultiselectCell"],
  ["radio", "InputRadioCell"],
]);

/**
 * Convert a static grid configuration into a computed one
 * - Infers the the required property from the validation schema (if exists).
 * - Infers the column field validator from the validation schema (if exists).
 * - Infers the cell from the editor (if exists).
 * @param config
 * @returns If config is already computed just returns a clone of it
 */
export function computeGridConfig<D extends GridDataItemRaw = GridDataItemRaw>(
  config: GridConfig<D>,
  options: ComputedGridConfigOptions<D>,
) {
  // without this we will get an infinity loop on $ck._.cloneDeep
  if ("computed" in config && config.computed) {
    return config as ComputedGridConfig<D>;
  }
  const newConfig = $ck._.cloneDeep(config);

  const columns = newConfig.columns.map((column) => {
    const field = isMappableColumn(column) ? column.field : undefined;
    const editor = isEditableColumn(column) ? column.editor : undefined;

    const fieldValidator =
      options.validationSchema && field
        ? $ck.utils.getZodFieldType(options.validationSchema, field)
        : undefined;

    const required =
      column.required ??
      (fieldValidator ? $ck.utils.isZodTypeRequired(fieldValidator) : false);

    const alignmentClasses = getColumnAlignmentClasses(column);
    const propagateErrorsFrom = field
      ? $ck._.compact(
          newConfig.columns.map((c) =>
            "propagateErrorsTo" in c && c.propagateErrorsTo === field
              ? c.field
              : undefined,
          ),
        )
      : [];

    const editorProps = editor
      ? getEditorProps(column as GridEditableColumn)
      : undefined;

    const cell =
      column.cell ?? (editor ? defaultEditorCells.get(editor) : undefined);

    const alwaysEditable =
      isEditableColumn(column) &&
      // Checkboxes and Radios should always be editable, to avoid the user having to click twice
      (column.alwaysEditable || ["boolean", "radio"].includes(editor ?? ""));

    return {
      ...column,
      headerCell: column.headerCell ?? "headerTemplate",
      default:
        column.default ?? newConfig.defaultRowData?.[field as StringKeyOf<D>],
      fieldValidator,
      required,
      field,
      editor,
      editorProps,
      alignmentClasses,
      alwaysEditable,
      propagateErrorsFrom: propagateErrorsFrom.length
        ? propagateErrorsFrom
        : undefined,
      cell,
      readonly: column.readonly ?? options.readonly,
    } as ComputedGridColumn<D, typeof column>;
  });

  const mappableColumns = columns.filter(isMappableColumn);
  const editableColumns = mappableColumns.filter(isEditableColumn);
  const importableColumns = mappableColumns.filter(isImportableColumn);

  const mappableColumnsHash = new Map(mappableColumns.map((c) => [c.field, c]));
  const editableColumnsHash = new Map(editableColumns.map((c) => [c.field, c]));
  const importableColumnsHash = new Map(
    importableColumns.map((c) => [c.field, c]),
  );

  return {
    ...newConfig,
    uniqueKey: getConfigUniqueKey(newConfig),
    computed: true,
    columns,
    mappableColumnsHash,
    editableColumnsHash,
    importableColumnsHash,
    options,
  } as ComputedGridConfig<D>;
}

export function getColumnTitleMinWidth(
  column: GridColumn,
  formatOptions?: I18nFormatOptions,
): number {
  const title = formatOptions?.t(column.title ?? "") || column.title || "";
  const titleMinWidth = title.length * 12 + 30; // 12 is an approximation of the average character width in pixels and 30 is for the padding and sorting icon
  return titleMinWidth;
}

/**
 * Obtain the next information from the column:
 * - underlyingType: The type of the column data based on the configuration (how data should be stored on DB).
 * - isRequired: If the column is required based on the configuration.
 * @param configColumn
 */
export function analyzeColumnUnderlyingType(configColumn: GridMappableColumn): {
  underlyingType: GridMappableColumn["type"];
  isRequired: boolean;
  fallback: InputSingleValue;
} {
  const zodTypeInfo = configColumn.fieldValidator
    ? $ck.utils.analyzeZodType(configColumn.fieldValidator)
    : undefined;

  if (zodTypeInfo?.type === "object" || zodTypeInfo?.type === "array") {
    devConsole.warn(
      `Invalid content type ${zodTypeInfo.type} in fieldValidator`,
      configColumn.fieldValidator,
    );
  }

  const zodStorageType =
    zodTypeInfo?.type !== "object" && zodTypeInfo?.type !== "array"
      ? zodTypeInfo?.type
      : undefined;

  const underlyingType = zodStorageType ?? configColumn.type;
  const isRequired = zodTypeInfo?.isRequired ?? configColumn.required ?? false;
  const defaults = {
    string: "",
    number: 0,
    boolean: false,
    date: undefined,
  };
  const fallback = !isRequired
    ? undefined
    : underlyingType
      ? (configColumn.default ?? defaults[underlyingType])
      : configColumn.default;

  // if fieldValidator is set, zod type takes precedence for underlyingType and isRequired
  return {
    underlyingType,
    isRequired,
    fallback: fallback as InputSingleValue, // TODO: multiselect test pending
  };
}

function tryParseCheckbox(
  rawContent: InputSingleValue,
  {
    checkedValue = true,
    uncheckedValue = false,
  }: {
    checkedValue?: InputSingleValue;
    uncheckedValue?: InputSingleValue;
  } = { checkedValue: true, uncheckedValue: false },
  options?: { fallback: InputSingleValue },
): InputSingleValue {
  const acceptedTrueValues: InputSingleValue[] = [
    checkedValue,
    true,
    1,
    "Y",
    "Yes",
    "T",
  ];
  const acceptedFalseValues: InputSingleValue[] = [
    uncheckedValue,
    false,
    0,
    "N",
    "No",
    "F",
  ];

  try {
    return acceptedTrueValues.some((v) => compareInputValues(rawContent, v))
      ? checkedValue
      : acceptedFalseValues.some((v) => compareInputValues(rawContent, v))
        ? uncheckedValue
        : rawContent;
  } catch (error) {
    devConsole.warn("Error parsing boolean", { rawContent, error, options });
    return options ? options.fallback : rawContent;
  }
}

function tryParseListItem<T extends InputSingleValue>(
  rawContent: T,
  items?: ListItemOptionOrPlainValue<T>[],
): InputSingleValue {
  if (!items) {
    return rawContent;
  }

  try {
    const { item: listItem } = findListItem(
      items,
      rawContent,
      (item, value) =>
        compareListItemValue(item, value) ||
        compareInputValues(getListItemOptionValue(item), value) ||
        compareInputValues(getListItemOptionLabel(item), value),
    );
    if (listItem === undefined || listItem === null) {
      return rawContent;
    }
    return getListItemOptionValue(listItem);
  } catch (error) {
    devConsole.warn("Error parsing list item", { rawContent, error });
    return rawContent;
  }
}

export function tryParseDate(
  rawContent: InputSingleValue,
  options?: { fallback: InputSingleValue },
): InputSingleValue {
  try {
    if (isDateStringOrNumber(rawContent)) {
      const result = new Date(rawContent);
      if (isNaN(result.getTime())) {
        return rawContent;
      }
      return result;
    }
    return rawContent;
  } catch (error) {
    devConsole.warn("Error parsing date", { rawContent, error, options });
    return options ? options.fallback : rawContent;
  }
}

function tryParseBoolean(
  rawContent: InputSingleValue,
  editorProps?: GridEditableColumn<"boolean">["editorProps"],
  options?: { fallback: InputSingleValue },
): InputSingleValue {
  const checkedValue =
    editorProps && "checkedValue" in editorProps
      ? editorProps.checkedValue
      : true;

  const uncheckedValue =
    editorProps && "uncheckedValue" in editorProps
      ? editorProps.uncheckedValue
      : false;

  const result = tryParseCheckbox(
    rawContent,
    { checkedValue, uncheckedValue },
    options,
  );
  if (typeof result === "boolean") return result;

  if (compareInputValues(result, checkedValue)) return true;
  if (compareInputValues(result, uncheckedValue)) return false;

  return result;
}

function tryParseNumber(
  rawContent: InputSingleValue,
  options?: { fallback: InputSingleValue },
): InputSingleValue {
  if (rawContent === undefined || rawContent === null) {
    return options ? options.fallback : rawContent;
  }
  if (typeof rawContent === "number") {
    return rawContent;
  }
  try {
    if (rawContent instanceof Date) return rawContent.getTime();

    const floatValue = parseFloat(rawContent.toString());
    return isNaN(floatValue) ? rawContent : floatValue;
  } catch (error) {
    devConsole.warn("Error parsing number", { rawContent, error, options });
    return options ? options.fallback : rawContent;
  }
}

export function tryParseString(
  rawContent: InputSingleValue,
  options?: { fallback: InputSingleValue },
): InputSingleValue {
  if (rawContent === undefined || rawContent === null) {
    return options ? options.fallback : rawContent;
  }
  if (typeof rawContent === "string") {
    return rawContent;
  }
  try {
    if (rawContent instanceof Date) return rawContent.toISOString();

    // TODO: this is not desirable from an end user perspective, we need to check the edge cases
    if (typeof rawContent === "object") return JSON.stringify(rawContent);

    return rawContent.toString();
  } catch (error) {
    devConsole.warn("Error parsing string", { rawContent, error, options });
    return options ? options.fallback : rawContent;
  }
}

/**
 * Normalizes the input value based on the data underlying type.
 * e.g if the column is a date, it will try to parse the input value to a date.
 * If the column is a number, it will try to parse the input value to a number.
 * If the column is a string, it will try to parse the input value to a string.
 * If the column is a boolean, it will try to parse the input value to a boolean.
 * Else it will return the raw content.
 * @param rawContent
 * @param configColumn
 * @returns
 */
function normalizeSingleInputForValidation(
  rawContent: InputSingleValue,
  configColumn: GridColumn,
): InputSingleValue {
  if (!isMappableColumn(configColumn)) {
    devConsole.warn("Column is not mappable", configColumn);
    return rawContent;
  }

  const { underlyingType, isRequired, fallback } =
    analyzeColumnUnderlyingType(configColumn);

  const options = configColumn.hidden ? { fallback } : undefined;
  switch (underlyingType) {
    case "date":
      return tryParseDate(rawContent, options);
    case "number":
      return tryParseNumber(rawContent, options);
    case "string":
      // Default value for required string is empty string
      return (
        tryParseString(rawContent, options) ?? (isRequired ? "" : rawContent)
      );
    case "boolean":
      // Default value for required boolean is false
      return (
        tryParseBoolean(
          rawContent,
          getEditableColumnEditorProps(configColumn, "boolean"),
          options,
        ) ?? (isRequired ? false : rawContent)
      );
    default:
      return rawContent ?? (options ? options.fallback : rawContent);
  }
}

export function normalizeInputForValidation(
  rawContent: InputValue,
  configColumn: GridColumn,
): InputValue {
  return Array.isArray(rawContent)
    ? rawContent.map((fieldContentItem) =>
        normalizeSingleInputForValidation(fieldContentItem, configColumn),
      )
    : normalizeSingleInputForValidation(rawContent, configColumn);
}

export function getEditableColumnRestrictedValues(
  configColumn: GridColumn,
): InputValue[] | undefined {
  const checkboxEditorProps = getEditableColumnEditorProps(
    configColumn,
    "boolean",
  );
  if (checkboxEditorProps) {
    const { checkedValue, uncheckedValue } = checkboxEditorProps;
    if ($ck._.isNil(checkedValue) || $ck._.isNil(uncheckedValue)) return;
    return [checkedValue, uncheckedValue];
  }

  const selectEditorProps =
    getEditableColumnEditorProps(configColumn, "select") ??
    getEditableColumnEditorProps(configColumn, "multiselect");
  if (selectEditorProps) {
    return selectEditorProps.items.map(getListItemOptionValue);
  }
}

/**
 * Detects the column display type based on the column configuration.
 * - if the column has no field, it returns the column type
 * - first it checks if the column has an editor, if so it returns the equivalent editor type
 * - if the column has a field, it checks the underlying type and returns it
 * @param rawContent
 * @param configColumn
 * @returns
 */
export function detectColumnDisplayType(
  configColumn: GridColumn,
): GridMappableColumn["type"] {
  if (!isMappableColumn(configColumn)) {
    return configColumn.type;
  }

  if (isEditableColumn(configColumn)) {
    switch (configColumn.editor) {
      case "select":
      case "multiselect":
      case "text":
        return "string";
      case "numeric":
        return "number";
      case "radio":
        return "boolean";
      default:
        return configColumn.editor;
    }
  }

  const { underlyingType } = analyzeColumnUnderlyingType(configColumn);

  return underlyingType;
}

export function getColumnAlignmentClasses(
  column: GridColumn,
  defaultAlignment = "text-left",
) {
  const columnType = detectColumnDisplayType(column);

  switch (columnType) {
    case "boolean":
    case "date":
      return "text-center";
    case "number":
      return "text-right";
    default:
      return defaultAlignment;
  }
}

export function getColumnEditorMinWidth(
  column: GridColumn,
  defaultMinWidth = 30,
): number {
  if (isEditableColumn(column)) {
    switch (column.editor) {
      case "text":
      case "numeric":
      case "date":
      case "boolean":
      case "select":
        if ("minWidthPx" in column && column.minWidthPx !== undefined) {
          return column.minWidthPx + 20; // adding extra space for editor padding and icons
        }
        return defaultMinWidth; // default min width for editable columns, can be adjusted based on design requirements
    }
  }
  return defaultMinWidth;
}

/**
 * Props for the editor component based on the editor type of the column
 * It contains the classes or the properties defined for each inline editor (type safe)
 */
export function getEditorProps(column: GridEditableColumn) {
  const defaultClasses = {
    boxClasses: "size-full",
    leftHelperClasses:
      "flex items-center gap-2 px-2 empty:hidden pointer-events-none",
    rightHelperClasses:
      "flex items-center gap-2 px-2 empty:hidden pointer-events-none",
    inputClasses:
      "size-full min-h-9 p-2 border-none rounded-none flex items-center gap-2 text-sm font-normal leading-5 bg-transparent focus:ring-0 focus:outline-none",
  };

  const defaultProps = {
    placeholder: column.placeholder,
    ariaLabel: column.title,
    ...column.editorProps,
  };

  switch (column.editor) {
    case "boolean":
      return {
        iconClasses: "check-icon !text-inherit text-xl",
        // default classes do not apply to checkbox editor
        ...defaultProps,
      };
    case "radio":
      return {
        iconClasses: "text-xl",
        // default classes do not apply to radio editor
        ...defaultProps,
      };
    case "text": {
      return {
        clearable: true,
        ...defaultClasses,
        ...defaultProps,
      };
    }
    case "numeric":
      return {
        type: "number",
        class: "text-right",
        clearable: true,
        ...defaultClasses,
        ...defaultProps,
      };
    case "date":
      return {
        "icon-placement": "end",
        ...defaultClasses,
        ...defaultProps,
      };
    case "select":
      return {
        ...defaultClasses,
        ...defaultProps,
      };
  }

  return { ...defaultClasses, ...defaultProps };
}

/**
 * Normalizes the input value based on the editor type.
 * e.g that way if the data comes as "2025-05-30" and the db type is a string, but the editor is date, I can pass it to date first then to string again to obtain "05-30-2025"
 * So even if the input is the correct type, we need to make sure it works for the editor
 */
export function normalizeInputForEditor(
  rawInputValue: InputValue,
  gridEditableColumn?: GridEditableColumn,
  formatOptions?: I18nFormatOptions,
) {
  if (!gridEditableColumn) {
    return rawInputValue;
  }
  const rawContent = rawInputValue as InputSingleValue;
  switch (gridEditableColumn.editor) {
    case "date":
      return tryParseDate(rawContent);
    case "numeric":
      return tryParseNumber(rawContent);
    case "text":
      return tryParseString(rawContent);
    case "boolean":
      return tryParseCheckbox(rawContent, gridEditableColumn.editorProps);
    case "select": {
      const items =
        gridEditableColumn.editorProps?.translateItems && formatOptions
          ? gridEditableColumn.editorProps?.items?.map((item) =>
              translateListItemOption(
                item as ListItemOptionOrPlainValue<InputSingleValue>,
                formatOptions,
              ),
            )
          : (gridEditableColumn.editorProps
              ?.items as ListItemOptionOrPlainValue<InputSingleValue>[]);
      return tryParseListItem(rawContent, items);
    }
    default:
      return rawContent;
  }
}

export function isMappableColumn<D extends GridDataItemRaw>(
  column: GridColumn<D> | ComputedGridColumn<D>,
): column is typeof column extends ComputedGridColumn<D>
  ? ComputedGridColumn<D, GridMappableColumn<D>>
  : GridMappableColumn<D> {
  return "field" in column && !$ck._.isNil(column.field);
}

export function isEditableColumn<D extends GridDataItemRaw>(
  column: GridColumn<D> | ComputedGridColumn<D>,
): column is typeof column extends ComputedGridColumn<D>
  ? ComputedGridColumn<D, GridEditableColumn>
  : GridEditableColumn {
  return "editor" in column && !$ck._.isNil(column.field);
}

export function isImportableColumn<D extends GridDataItemRaw>(
  column: GridColumn<D>,
): column is GridMappableColumn<D> {
  return (
    isMappableColumn(column) &&
    // isKey should be importable but is not required to be present
    // is very important that hidden columns are not importable (except for isKey)
    !column.field.startsWith("$") &&
    !(column.hidden && !column.isKey)
  );
}

export function isColumnOfEditorType<
  E extends GridColumnEditorType,
  D extends GridDataItemRaw,
  F extends StringKeyOf<D> = StringKeyOf<D>,
>(
  column: ComputedGridColumn<D>,
  type: E,
): column is ComputedGridColumn<
  D,
  GridEditableColumn<E, D[F] & GridColumnCellValue<E>, F>
>;

// Base overload: Plain GridColumn
export function isColumnOfEditorType<
  E extends GridColumnEditorType,
  D extends GridDataItemRaw,
  F extends StringKeyOf<D> = StringKeyOf<D>,
>(
  column: GridColumn,
  type: E,
): column is GridEditableColumn<E, D[F] & GridColumnCellValue<E>, F>;

export function isColumnOfEditorType<E extends GridColumnEditorType>(
  column: GridColumn | ComputedGridColumn,
  type: E,
): boolean {
  return isEditableColumn(column) && column.editor === type;
}
function getEditableColumnEditorProps<E extends GridColumnEditorType>(
  column: GridColumn | undefined,
  type: E,
): GridEditableColumn<E>["editorProps"] | undefined {
  if (!column || !isColumnOfEditorType(column, type)) return undefined;
  return column.editorProps;
}

export function getConfigUniqueKey<D extends GridDataItemRaw>(
  gridConfig: GridConfig<D>,
): StringKeyOf<D>[] {
  return gridConfig.columns
    .map((column) =>
      isMappableColumn(column) && column.isKey
        ? (column.field as StringKeyOf<D>)
        : undefined,
    )
    .filter((field) => field !== undefined);
}
